import { Plus, Trash2 } from "lucide-react";
import { gbp } from "../../lib/api";
import UnitSelect from "./UnitSelect";
import LinePackaging from "./LinePackaging";
import { isGenericEach } from "../products/PackagingEditor";

export function confClass(level, value) {
  const empty = value === null || value === undefined || value === "";
  if (empty) return "field-missing";
  if (level === "low") return "field-low";
  if (level === "medium") return "field-medium";
  return "";
}

// QTY | PURCHASE UNIT | PACKAGING (packs × items × size, hierarchy editor) | UNIT PRICE | TOTAL
const COLS = [
  ["product_code", "Code", "w-24", "text"],
  ["description", "Description (original)", "min-w-[200px]", "text"],
  ["quantity", "Qty", "w-16", "number"],
  ["unit", "Purchase unit", "w-28", "text"],
  ["packaging", "Packaging", "min-w-[250px]", "custom"],
  ["unit_price", "Unit £", "w-24", "number"],
  ["line_total", "Total £", "w-24", "number"],
  ["vat_rate", "VAT %", "w-16", "number"],
];
const OPTIONAL = new Set(["product_code", "vat_rate"]);
const MONEY = new Set(["unit_price", "line_total"]);

export function totalUnits(ln) {
  const q = Number(ln.quantity), n = Number(ln.units_per_pack);
  if (!q || !(n > 1)) return null;
  return Math.round(q * n * 1000) / 1000;
}

function unitsHint(ln) {
  if (isGenericEach(ln.unit) && !ln.each_means_unit) return null; // EACH = one item unless the owner opted in: no "= 100 pieces" hint
  const t = totalUnits(ln);
  if (t == null) return null;
  const type = (ln.item_type || "").toLowerCase();
  const word = type && type !== "other" ? `${type}${Math.abs(t) === 1 ? "" : "s"}` : "units";
  const each = ln.unit_price != null && ln.units_per_pack > 1 ? ` · ${gbp(ln.unit_price / ln.units_per_pack)} each` : "";
  return `= ${t} ${word}${each}`;
}

const EMPTY = { product_code: "", description: "", quantity: null, unit: "", units_per_pack: null, packs_per_unit: null, items_per_pack: null, item_type: "", each_size: "", size_is_capacity: null, pack_size: "", unit_price: null, line_total: null, vat_rate: null, confidence: {} };

export default function LinesTable({ lines, onChange, readOnly }) {
  const update = (i, key, val) => {
    const copy = lines.slice();
    copy[i] = { ...copy[i], [key]: val };
    onChange(copy);
  };
  const addLine = () => onChange([...lines, { id: crypto.randomUUID(), ...EMPTY }]);
  const remove = (i) => onChange(lines.filter((_, j) => j !== i));
  const sum = lines.reduce((a, l) => a + (Number(l.line_total) || 0), 0);

  const replaceLine = (i, next) => { const copy = lines.slice(); copy[i] = next; onChange(copy); };
  // line discount: unit_price is net of the printed line discount; the printed (list) price is shown underneath
  const discountNote = (ln, i) => {
    if (ln.discount_review) return <span className="block px-2 text-[10px] text-amber-700" title={ln.discount_review} data-testid={`line-${i}-discount-review`}>discount £{Number(ln.discount || 0).toFixed(2)} printed — check</span>;
    if (ln.list_unit_price != null && ln.discount_amount) return <span className="block px-2 text-[10px] text-emerald-700" data-testid={`line-${i}-discount`}>list {gbp(ln.list_unit_price)} − {gbp(ln.discount_amount)} discount</span>;
    return null;
  };
  const cell = (ln, i, k, type) => {
    if (k === "packaging") return <LinePackaging line={ln} index={i} onChange={(next) => replaceLine(i, next)} readOnly={readOnly} />;
    if (readOnly) {
      const v = MONEY.has(k) ? gbp(ln[k]) : ln[k] ?? "—";
      return <><span className={`block px-2 py-1 ${k === "description" ? "" : "font-mono"}`}>{v}</span>{k === "unit_price" && discountNote(ln, i)}</>;
    }
    if (k === "unit_price") return <>{input(ln, i, k, type)}{discountNote(ln, i)}</>;
    if (k === "unit") return <UnitSelect value={ln.unit} onChange={(v) => update(i, "unit", v)} testId={`line-${i}-unit`} className={confClass(ln.confidence?.unit, ln.unit)} />;
    return input(ln, i, k, type);
  };
  const input = (ln, i, k, type) => {
    return (
      <input
        type={type === "number" ? "number" : "text"} step="any"
        value={ln[k] ?? ""}
        onChange={(e) => update(i, k, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
        data-testid={`line-${i}-${k}`}
        className={`w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${type === "number" ? "font-mono text-right" : ""} ${OPTIONAL.has(k) ? "" : confClass(ln.confidence?.[k], ln[k])}`}
      />
    );
  };

  return (
    <div data-testid="lines-table" className="min-w-0 max-w-full">
      <div className="overflow-x-auto max-w-full border border-slate-200 rounded-lg bg-white">
        <table className="w-full min-w-[1040px] text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{COLS.map(([k, l, w]) => <th key={k} className={`px-2 py-2 ${w}`}>{l}</th>)}{!readOnly && <th className="w-10" />}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.length === 0 && <tr><td colSpan={COLS.length + 1} className="px-3 py-6 text-center text-slate-500" data-testid="lines-empty">No product lines. Add them manually below.</td></tr>}
            {lines.map((ln, i) => {
              const hint = unitsHint(ln);
              const promo = !!ln.is_promo_discount;
              return (
                <tr key={ln.id || i} data-testid={`line-row-${i}`} className={promo ? "bg-violet-50" : (!readOnly && !(ln.unit || "").trim() ? "bg-rose-50" : "")}>
                  {COLS.map(([k, , w, type]) => (
                    <td key={k} className={`p-1 align-top ${w}`}>
                      {promo && k === "description" ? <span className="block px-2 py-1 text-sm text-violet-900" data-testid={`line-${i}-promo`}><span className="font-semibold">{ln.description}</span><span className="ml-2 inline-flex items-center rounded-full bg-violet-100 text-violet-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Promo discount</span></span>
                       : promo && k === "unit" ? <span className="block px-2 py-1 text-xs text-violet-700 italic" data-testid={`line-${i}-unit-promo`}>— not a product —</span>
                       : promo && k === "packaging" ? <span className="block px-2 py-1 text-xs text-violet-700 italic">no stock, no price</span>
                       : promo && (k === "product_code" || k === "quantity" || k === "unit_price" || k === "vat_rate") ? <span className="block px-2 py-1 text-xs text-slate-400 text-right">—</span>
                       : cell(ln, i, k, type)}
                      {!promo && k === "packaging" && hint && <p className="mt-0.5 whitespace-nowrap text-[10px] font-medium text-emerald-700" data-testid={`line-${i}-total-units`}>{hint}</p>}
                    </td>
                  ))}
                  {!readOnly && <td className="p-1 text-center"><button onClick={() => remove(i)} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`line-${i}-remove`}><Trash2 className="h-4 w-4" /></button></td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50 text-sm">
            <tr><td colSpan={6} className="px-3 py-2 text-right text-slate-500">Sum of lines</td><td className="px-3 py-2 text-right font-mono font-semibold" data-testid="lines-sum">{gbp(sum)}</td><td colSpan={2} /></tr>
          </tfoot>
        </table>
      </div>
      {!readOnly && <button onClick={addLine} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline" data-testid="add-line-button"><Plus className="h-4 w-4" /> Add line</button>}
      <p className="mt-1.5 text-[11px] text-slate-500">Purchase unit = what the supplier billed per (box, case, SBOX, kg…). Packaging describes what is inside one purchase unit — optional inner packs × items × each size (e.g. 4 packs × 75 pieces, or 24 × 330ml cans). Lines marked “Differs from saved product” keep the invoice's own packaging until you decide.</p>
    </div>
  );
}
